import SyncIcon from "@mui/icons-material/Sync";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import {
  AdminPaddleSyncPreview,
  AdminPaddleUserSyncPreview,
  AdminPaddleUserSyncPreviewItem,
  AdminPaddleSyncPriceSummary,
  AdminUser,
  AdminSubscription,
} from "../admin.api";

interface PaddleSyncPreviewDialogProps {
  open: boolean;
  preview: AdminPaddleUserSyncPreview | null;
  loading: boolean;
  syncing: boolean;
  error: string | null;
  user: AdminUser;
  subscriptions: AdminSubscription[];
  onClose: () => void;
  onConfirm: () => void;
}

function formatValue(value: unknown): string {
  if (value === null || value === undefined || value === "") {
    return "—";
  }

  if (typeof value === "string") {
    return value;
  }

  return JSON.stringify(value, null, 2);
}

function formatDateLabel(value?: string | null): string {
  if (!value) {
    return "—";
  }

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleString();
}

function formatImageAllowance(preview: AdminPaddleSyncPreview["metadata"]): string {
  return preview.hasUnlimitedImages
    ? "Unlimited"
    : String(preview.aiImageCredits);
}

function formatPlanLabel(subscription?: AdminSubscription): string {
  return subscription?.planId?.name || subscription?.planId || "Unknown plan";
}

function ChangeList({
  title,
  items,
}: {
  title: string;
  items: Array<{
    field?: string;
    label?: string;
    current?: unknown;
    next: unknown;
    reason?: string;
  }>;
}) {
  if (items.length === 0) {
    return null;
  }

  return (
    <Box>
      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
        {title}
      </Typography>
      <Stack spacing={1.5}>
        {items.map((item, index) => (
          <Paper
            key={`${item.field || item.label || "item"}-${index}`}
            variant="outlined"
            sx={{ p: 1.5, borderRadius: 2 }}
          >
            <Typography variant="body2" sx={{ fontWeight: 700, mb: 1 }}>
              {item.field || item.label}
            </Typography>
            {item.current !== undefined && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
              >
                Current: {formatValue(item.current)}
              </Typography>
            )}
            <Typography
              variant="body2"
              sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word", mt: 0.5 }}
            >
              Next: {formatValue(item.next)}
            </Typography>
            {item.reason && (
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                {item.reason}
              </Typography>
            )}
          </Paper>
        ))}
      </Stack>
    </Box>
  );
}

function PriceList({
  items,
  validitySourcePriceId,
}: {
  items: AdminPaddleSyncPriceSummary[];
  validitySourcePriceId: string | null;
}) {
  if (items.length === 0) {
    return null;
  }

  return (
    <Box>
      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
        Price Breakdown
      </Typography>
      <Stack spacing={1.5}>
        {items.map((item) => (
          <Paper key={item.priceId} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 1 }}>
              <Chip
                label={item.shortName || item.name}
                color={item.kind === "primary" ? "primary" : "default"}
                variant={item.kind === "primary" ? "filled" : "outlined"}
              />
              {item.kind && <Chip label={item.kind} size="small" variant="outlined" />}
              {item.status && <Chip label={item.status} size="small" variant="outlined" />}
              {item.billingInterval && (
                <Chip label={`Interval: ${item.billingInterval}`} size="small" variant="outlined" />
              )}
              {item.aiImageCredits > 0 && (
                <Chip
                  label={item.aiImageCredits >= 1000000 ? "Images: Unlimited" : `Images: ${item.aiImageCredits}`}
                  size="small"
                  color={item.aiImageCredits >= 1000000 ? "success" : "default"}
                  variant="outlined"
                />
              )}
              {item.articleCredits > 0 && (
                <Chip label={`Articles: ${item.articleCredits}`} size="small" variant="outlined" />
              )}
              {item.disablePig && (
                <Chip label="Tool credits disabled" size="small" color="warning" variant="outlined" />
              )}
              {validitySourcePriceId === item.priceId && (
                <Chip label="Validity source" size="small" color="success" />
              )}
            </Box>

            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              {item.priceId}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Effective start: {formatDateLabel(item.effectiveStartsAt)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Effective end: {formatDateLabel(item.effectiveEndsAt)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Next billed at: {formatDateLabel(item.nextBilledAt)}
            </Typography>
            {item.afterEnd && (
              <Typography variant="body2" color="text.secondary">
                After end: {item.afterEnd}
              </Typography>
            )}
            {Object.keys(item.customData || {}).length > 0 && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mt: 1, whiteSpace: "pre-wrap", wordBreak: "break-word" }}
              >
                Custom data: {formatValue(item.customData)}
              </Typography>
            )}
          </Paper>
        ))}
      </Stack>
    </Box>
  );
}

export default function PaddleSyncPreviewDialog({
  open,
  preview,
  loading,
  syncing,
  error,
  user,
  subscriptions,
  onClose,
  onConfirm,
}: PaddleSyncPreviewDialogProps) {
  const subscriptionsById = new Map(
    subscriptions.map((subscription) => [subscription._id, subscription]),
  );

  const renderSubscriptionPreview = (
    item: AdminPaddleUserSyncPreviewItem,
    index: number,
  ) => {
    const subscription = subscriptionsById.get(item.subscriptionId);
    const previewItem = item.preview;

    return (
      <Paper key={item.subscriptionId} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
        <Stack spacing={2}>
          <Box>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 1 }}>
              <Chip label={formatPlanLabel(subscription)} color="primary" />
              {subscription?.status && (
                <Chip label={`Local: ${subscription.status}`} variant="outlined" />
              )}
              {item.matchedPaddleSubscriptionId && (
                <Chip
                  label={`Paddle: ${item.matchedPaddleSubscriptionId}`}
                  variant="outlined"
                />
              )}
              {item.matchedVia && (
                <Chip label={`Matched via: ${item.matchedVia}`} variant="outlined" />
              )}
              {item.matchedCustomerId && (
                <Chip label={`Customer: ${item.matchedCustomerId}`} variant="outlined" />
              )}
            </Box>
            <Typography variant="body2" color="text.secondary">
              Local subscription {index + 1}: {item.subscriptionId}
            </Typography>
          </Box>

          {item.error && <Alert severity="warning">{item.error}</Alert>}

          {previewItem && (
            <>
              <Box>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 1.5 }}>
                  <Chip label={`Paddle: ${previewItem.paddleStatus}`} color="primary" variant="outlined" />
                  <Chip
                    label={`Mode: ${previewItem.mode}`}
                    color={previewItem.mode === "cancelled" ? "warning" : "success"}
                  />
                  {previewItem.metadata.billingInterval && (
                    <Chip label={`Interval: ${previewItem.metadata.billingInterval}`} variant="outlined" />
                  )}
                  {previewItem.metadata.keepAccessUntil && (
                    <Chip
                      label={`Access until: ${new Date(previewItem.metadata.keepAccessUntil).toLocaleDateString()}`}
                      variant="outlined"
                    />
                  )}
                  <Chip label={`Articles: ${previewItem.metadata.articleCredits}`} variant="outlined" />
                  <Chip
                    label={
                      previewItem.metadata.hasUnlimitedImages
                        ? "Images: Unlimited"
                        : `Images: ${formatImageAllowance(previewItem.metadata)}`
                    }
                    color={previewItem.metadata.hasUnlimitedImages ? "success" : "default"}
                    variant="outlined"
                  />
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {previewItem.summary}
                </Typography>
              </Box>

              {previewItem.warnings.length > 0 && (
                <Stack spacing={1}>
                  {previewItem.warnings.map((warning, warningIndex) => (
                    <Alert key={`${item.subscriptionId}-${warning}-${warningIndex}`} severity="warning">
                      {warning}
                    </Alert>
                  ))}
                </Stack>
              )}

              <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
                  Paddle Data
                </Typography>
                <Stack spacing={0.75}>
                  <Typography variant="body2" color="text.secondary">
                    Billing period start: {formatDateLabel(previewItem.metadata.currentBillingPeriodStart)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Billing period end: {formatDateLabel(previewItem.metadata.currentBillingPeriodEnd)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Next billed at: {formatDateLabel(previewItem.metadata.nextBilledAt)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Article credits: {previewItem.metadata.articleCredits}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    AI image credits: {formatImageAllowance(previewItem.metadata)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Reset AI image usage: {previewItem.metadata.willResetAiImageUsage ? "Yes" : "No"}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Validity source: {previewItem.metadata.validitySourcePriceName || "—"}
                    {previewItem.metadata.validitySourcePriceId ? ` (${previewItem.metadata.validitySourcePriceId})` : ""}
                  </Typography>
                </Stack>
              </Paper>

              <PriceList
                items={previewItem.metadata.priceSummaries}
                validitySourcePriceId={previewItem.metadata.validitySourcePriceId}
              />
              <ChangeList title="Subscription Changes" items={previewItem.subscriptionChanges} />
              <ChangeList title="User Changes" items={previewItem.userChanges} />
              <ChangeList title="Side Effects" items={previewItem.sideEffects} />

              {previewItem.subscriptionChanges.length === 0 &&
                previewItem.userChanges.length === 0 &&
                previewItem.sideEffects.length === 0 && (
                  <Alert severity="info">
                    No local changes were detected from this sync preview.
                  </Alert>
                )}
            </>
          )}
        </Stack>
      </Paper>
    );
  };

  return (
    <Dialog open={open} onClose={syncing ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <SyncIcon color="primary" />
          <Typography variant="h6">Preview User Paddle Sync</Typography>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Stack spacing={2}>
            {error && <Alert severity="error">{error}</Alert>}

            <Alert severity="info">
              Reviewing Paddle sync for <strong>{user.email}</strong>
            </Alert>

            {preview && (
              <>
                <Box>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 1.5 }}>
                    <Chip label={`Local subscriptions: ${preview.metadata.totalLocalSubscriptions}`} variant="outlined" />
                    <Chip label={`Matched: ${preview.metadata.matchedLocalSubscriptions}`} color="success" variant="outlined" />
                    <Chip label={`Unmatched local: ${preview.metadata.unmatchedLocalSubscriptions}`} color={preview.metadata.unmatchedLocalSubscriptions > 0 ? "warning" : "default"} variant="outlined" />
                    <Chip label={`Unmatched Paddle: ${preview.metadata.unmatchedPaddleSubscriptions}`} color={preview.metadata.unmatchedPaddleSubscriptions > 0 ? "warning" : "default"} variant="outlined" />
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {preview.summary}
                  </Typography>
                </Box>

                {preview.warnings.length > 0 && (
                  <Stack spacing={1}>
                    {preview.warnings.map((warning, index) => (
                      <Alert key={`${warning}-${index}`} severity="warning">
                        {warning}
                      </Alert>
                    ))}
                  </Stack>
                )}

                {preview.unmatchedPaddleSubscriptions.length > 0 && (
                  <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
                      New Local Subscriptions To Create
                    </Typography>
                    <Stack spacing={1}>
                      {preview.unmatchedPaddleSubscriptions.map((subscription) => (
                        <Typography
                          key={subscription.paddleSubscriptionId}
                          variant="body2"
                          color="text.secondary"
                        >
                          {subscription.paddleSubscriptionId} ({subscription.status})
                          {subscription.keepAccessUntil
                            ? ` access until ${new Date(subscription.keepAccessUntil).toLocaleDateString()}`
                            : ""}
                          {subscription.billingInterval
                            ? ` interval ${subscription.billingInterval}`
                            : ""}
                          {subscription.customerId ? ` customer ${subscription.customerId}` : ""}
                        </Typography>
                      ))}
                    </Stack>
                  </Paper>
                )}

                <Divider />

                <Stack spacing={2}>
                  {preview.subscriptionPreviews.map(renderSubscriptionPreview)}
                </Stack>
              </>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={syncing}>
          Close
        </Button>
        <Button
          variant="contained"
          onClick={onConfirm}
          disabled={loading || syncing || !preview}
          startIcon={syncing ? <CircularProgress size={18} color="inherit" /> : <SyncIcon />}
        >
          {syncing ? "Syncing..." : "Sync User With Paddle"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
